/**
 * AnalyticsTab - Campaign analytics, conversion metrics, and trends
 */
import { Users, TrendingUp, Mail, Phone, Target, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getThemeByKey, LEGACY_CAMPAIGN_THEMES } from './campaignUtils';

export default function AnalyticsTab({
  report,
  analyticsData,
  conversionMetrics,
  loadingAnalytics,
  onRefreshAnalytics
}) {
  if (loadingAnalytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <p className="text-sm text-muted-foreground">Today's Signups</p>
          </div>
          <p className="text-3xl font-bold text-foreground">{analyticsData?.overview?.today_signups || 0}</p>
        </div>
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-sm text-muted-foreground">This Week</p>
          </div>
          <p className="text-3xl font-bold text-foreground">{analyticsData?.overview?.week_signups || 0}</p>
        </div>
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-muted-foreground">Email Delivery</p>
          </div>
          <p className="text-3xl font-bold text-foreground">{analyticsData?.overview?.email_delivery_rate || 0}%</p>
        </div>
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Phone className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-sm text-muted-foreground">SMS Delivery</p>
          </div>
          <p className="text-3xl font-bold text-foreground">{analyticsData?.overview?.sms_delivery_rate || 0}%</p>
        </div>
      </div>

      {/* Conversion Metrics */}
      {conversionMetrics?.metrics && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-6">
            <Target className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-xl text-foreground">Conversion Metrics</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {conversionMetrics.metrics.map(metric => {
              const themeKey = report?.campaigns?.find(c => c.campaign_id === metric.campaign_id)?.theme || 'teal';
              const themeOption = getThemeByKey(themeKey);
              const theme = LEGACY_CAMPAIGN_THEMES[metric.campaign_id] || {
                gradient: themeOption.gradient,
                color: themeOption.textColor,
                bg: themeOption.bg,
                border: themeOption.border
              };
              return (
                <div key={metric.campaign_id} className={`rounded-xl p-4 ${theme.bg} dark:bg-opacity-20 border ${theme.border}`}>
                  <p className={`font-medium ${theme.color} mb-3`}>Campaign {metric.campaign_id}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Views:</span>
                      <span className="font-medium text-foreground">{metric.total_views}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Signups:</span>
                      <span className="font-medium text-foreground">{metric.total_signups}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2 mt-2">
                      <span className="text-muted-foreground">Conversion:</span>
                      <span className={`font-bold ${metric.conversion_rate > 5 ? 'text-green-600' : metric.conversion_rate > 2 ? 'text-amber-600' : 'text-red-600'}`}>
                        {metric.conversion_rate}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Recent (7d):</span>
                      <span className={`font-medium ${metric.recent_conversion_rate > 5 ? 'text-green-600' : 'text-foreground'}`}>
                        {metric.recent_conversion_rate}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Campaign Performance Details */}
      {analyticsData?.campaigns && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-xl text-foreground">Campaign Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Campaign</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">Signups</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">Email %</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">SMS %</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">Referrals</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">Referral %</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.campaigns.map(campaign => {
                  const themeKey = campaign.theme || report?.campaigns?.find(c => c.campaign_id === campaign.campaign_id)?.theme || 'teal';
                  const themeOption = getThemeByKey(themeKey);
                  const theme = LEGACY_CAMPAIGN_THEMES[campaign.campaign_id] || {
                    gradient: themeOption.gradient,
                    color: themeOption.textColor,
                    bg: themeOption.bg,
                    border: themeOption.border
                  };
                  return (
                    <tr key={campaign.campaign_id} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${theme.gradient}`} />
                          <span className="font-medium text-foreground">{campaign.title?.slice(0, 30)}...</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-foreground">{campaign.total_signups}</td>
                      <td className="text-right py-3 px-4 text-foreground">{campaign.email_delivery_rate}%</td>
                      <td className="text-right py-3 px-4 text-foreground">{campaign.sms_delivery_rate}%</td>
                      <td className="text-right py-3 px-4 text-foreground">{campaign.referral_signups}</td>
                      <td className="text-right py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          campaign.referral_rate > 10 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          campaign.referral_rate > 5 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {campaign.referral_rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily Trend */}
      {analyticsData?.daily_trend?.length > 0 && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h2 className="font-heading text-xl text-foreground mb-6">7-Day Signup Trend</h2>
          <div className="flex items-end gap-2 h-40">
            {analyticsData.daily_trend.map((day, idx) => {
              const maxSignups = Math.max(...analyticsData.daily_trend.map(d => d.signups)) || 1;
              const height = (day.signups / maxSignups) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex justify-center">
                    <span className="text-xs text-muted-foreground">{day.signups}</span>
                  </div>
                  <div 
                    className="w-full bg-gradient-to-t from-primary to-primary/60 rounded-t-lg transition-all"
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <span className="text-xs text-muted-foreground">{day.date?.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Country Distribution */}
      {analyticsData?.country_distribution?.length > 0 && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h2 className="font-heading text-xl text-foreground mb-6">Geographic Distribution</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {analyticsData.country_distribution.map((country, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-foreground">{country.country}</span>
                <span className="font-bold text-primary">{country.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button 
          variant="outline" 
          onClick={onRefreshAnalytics}
          disabled={loadingAnalytics}
          className="rounded-full"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Refresh Analytics
        </Button>
      </div>
    </div>
  );
}
